import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    LinearProgress,
    Alert
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { SESSION_TIMEOUTS } from '../utils/constants';

const SessionTimeoutWarning = ({ open, onStayLoggedIn, onLogout, timeRemaining }) => {
    const [countdown, setCountdown] = useState(timeRemaining);
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        setCountdown(timeRemaining);
    }, [timeRemaining]);

    useEffect(() => {
        if (!open || countdown <= 0) return;

        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    onLogout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [open, countdown, onLogout]);

    const handleStayLoggedIn = async () => {
        setIsRefreshing(true);
        try {
            await onStayLoggedIn();
        } catch (error) {
            console.error('Failed to extend session:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progressValue = (countdown / (SESSION_TIMEOUTS.WARNING_BEFORE_LOGOUT / 1000)) * 100;

    return (
        <Dialog 
            open={open} 
            maxWidth="sm" 
            fullWidth
            disableEscapeKeyDown
            disableBackdropClick
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <Warning color="warning" />
                    <Typography variant="h6">
                        Session Timeout Warning
                    </Typography>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Your session will expire due to inactivity. You will be automatically logged out in:
                </Alert>
                
                <Box textAlign="center" mb={3}>
                    <Typography variant="h3" color="warning.main" fontWeight="bold">
                        {formatTime(countdown)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        minutes remaining
                    </Typography>
                </Box>

                <LinearProgress 
                    variant="determinate" 
                    value={progressValue}
                    sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: 'warning.light',
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: 'warning.main'
                        }
                    }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    Click "Stay Logged In" to continue your session, or you will be automatically logged out.
                </Typography>
            </DialogContent>
            
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button 
                    onClick={onLogout}
                    variant="outlined"
                    color="inherit"
                >
                    Logout Now
                </Button>
                <Button 
                    onClick={handleStayLoggedIn}
                    variant="contained"
                    color="primary"
                    disabled={isRefreshing}
                >
                    {isRefreshing ? 'Extending...' : 'Stay Logged In'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionTimeoutWarning;
